import { useCallback, useEffect, useRef } from 'react';

const dirtyEditors = new Set<symbol>();
const pendingSaves = new Set<symbol>();
let allowImmediateNavigation = false;
const discardMessage = 'Hai modifiche non salvate. Vuoi uscire senza salvarle?';
const pendingSaveMessage = 'Il salvataggio è in corso. Attendi l’esito prima di cambiare pagina.';

/** A POST already in flight cannot be undone by a discard confirmation. */
export function blockNavigationDuringSave(): () => void {
  const identity = Symbol('crm-pending-save');
  pendingSaves.add(identity);
  return () => { pendingSaves.delete(identity); };
}

/** Shared by all CRM navigation calls through the root Wouter router. */
export function allowNavigationWithUnsavedChanges(): boolean {
  if (pendingSaves.size > 0) {
    window.alert(pendingSaveMessage);
    return false;
  }
  if (allowImmediateNavigation) {
    allowImmediateNavigation = false;
    return true;
  }
  return dirtyEditors.size === 0 || window.confirm(discardMessage);
}

export function allowNextNavigationAfterConfirmedClose(): void {
  allowImmediateNavigation = true;
  queueMicrotask(() => { allowImmediateNavigation = false; });
}

export function useUnsavedNavigationRegistration(active: boolean, isDirty: boolean): void {
  const identity = useRef(Symbol('crm-unsaved-editor'));
  useEffect(() => {
    if (!active || !isDirty) return;
    dirtyEditors.add(identity.current);
    return () => { dirtyEditors.delete(identity.current); };
  }, [active, isDirty]);
}

/**
 * Shared guard for editing forms. The caller must pass actual unsaved state,
 * not merely whether a record exists or a request is pending.
 */
export function useUnsavedChangesGuard(active: boolean, isDirty: boolean, onOpenChange: (open: boolean) => void) {
  const allowSavedClose = useRef(false);
  useUnsavedNavigationRegistration(active, isDirty);

  useEffect(() => {
    if (!active) {
      allowSavedClose.current = false;
      return;
    }
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active, isDirty]);

  const requestClose = useCallback((nextOpen: boolean): boolean => {
    if (nextOpen) {
      onOpenChange(true);
      return true;
    }
    if (!allowSavedClose.current && isDirty && !window.confirm(discardMessage)) return false;
    allowSavedClose.current = false;
    allowNextNavigationAfterConfirmedClose();
    onOpenChange(false);
    return true;
  }, [isDirty, onOpenChange]);

  const closeAfterSave = useCallback(() => {
    allowSavedClose.current = true;
    allowNextNavigationAfterConfirmedClose();
    onOpenChange(false);
    allowSavedClose.current = false;
  }, [onOpenChange]);

  return { requestClose, closeAfterSave };
}
